import type { ReactElement } from "react";
import { useSearchParams } from "react-router";

import { localReturnUrl } from "../../app/return-after-register.js";
import { expiredMessage } from "../../app/session-expiry.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { loginRules } from "../../forms/view-model-rules.js";
import { accountRegisterUrl } from "../tenant-users/urls.js";

/**
 * ログイン（1 段目）。<b>次の画面は API が決める</b>（確認コード・認証アプリの登録・ロックアウト）。
 */
export function LoginPage(): ReactElement {
    usePageMeta("ログイン");
    const form = useApiForm("/api/account/login", loginRules);
    // ログインが切れて送られてきたときの理由（一定時間操作がなかった）
    const [search] = useSearchParams();
    const expired = expiredMessage(search.get("reason"));
    // 戻り先（料金プランの「この内容で申し込む」・招待メールのリンクなど）は、アカウント登録へ進んでも持っていく
    const returnUrl = localReturnUrl(search.get("returnUrl") ?? undefined);

    return (
        <div className="page max-w-md">
            <div className="panel">
                <h1 className="page-title">ログイン</h1>
                <p className="page-lead">Pleasanter.net のアカウントでログインします。</p>

                {expired === null ? null : <div className="alert-info mb-4" role="status">{expired}</div>}

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />

                    <TextField
                        label="メールアドレス" name="Email" type="email" required
                        placeholder="mail@example.com" autoComplete="username" autoFocus
                        error={form.fieldError("Email")}
                    />
                    <TextField
                        label="パスワード" name="Password" type="password" required
                        placeholder="••••••••••" autoComplete="current-password"
                        error={form.fieldError("Password")}
                    />

                    <div className="mb-7 flex items-center gap-2">
                        <input className="form-check" type="checkbox" id="RememberMe" name="RememberMe" value="true" />
                        <label className="text-sm text-slate-600" htmlFor="RememberMe">ログインしたままにする</label>
                    </div>

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>
                        ログイン
                    </button>
                </form>

                {/* 操作の入口は文中のリンクにせず、短い説明とボタンを 1 行ずつ並べる（docs/01 §6） */}
                <div className="panel-footer space-y-3">
                    <div className="flex items-center justify-between gap-4">
                        <p>アカウントをお持ちでない方</p>
                        <a href={accountRegisterUrl(returnUrl)} className="btn-secondary btn-sm shrink-0">アカウント登録</a>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <p>パスワードを再設定する</p>
                        <a href="/Account/ForgotPassword" className="btn-secondary btn-sm shrink-0">パスワードをお忘れの方</a>
                    </div>
                </div>
            </div>
        </div>
    );
}
